import styles from "./OfflineBanner.module.css";
import { BANNER_MESSAGES } from "~/modules/checkout/constants";
import { OrderSource } from "~/entities/orderEntities";
import { Site } from "~/entities/siteEntities";
import { getNextAvailableTime } from "~/helpers/menuHelpers";
import {
  getServiceStatus,
  isServiceTypeAvailable,
} from "~/helpers/siteHelpers";
import { Menu } from "~/entities/menuEntities";

interface OfflineBannerProps {
  site: Site;
  orderSource: OrderSource;
  orderTypeCode: string;
  menu?: Menu | null;
  requestedReadyTime?: string | null;
  noTimeSlots: boolean;
}

const OfflineBanner = ({
  site,
  orderSource,
  orderTypeCode,
  menu,
  requestedReadyTime,
  noTimeSlots,
}: OfflineBannerProps) => {
  const { menuOpen } = getServiceStatus(
    site,
    menu,
    orderSource,
    requestedReadyTime
  );
  const serviceAvailable = isServiceTypeAvailable(site, orderSource);

  const getMessage = () => {
    if (!serviceAvailable || noTimeSlots) {
      const baseMessage =
        orderSource === OrderSource.TABLE_ORDERING
          ? BANNER_MESSAGES.TABLE_ORDERING_UNAVAILABLE
          : BANNER_MESSAGES.ONLINE_ORDERING_UNAVAILABLE;

      const resumeTime = site?.serviceAvailability?.find(
        (s) => s.orderTypeCode === orderTypeCode
      )?.appliesUntil
        ? new Date(
            site.serviceAvailability.find(
              (s) => s.orderTypeCode === orderTypeCode
            )!.appliesUntil!
          ).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
          })
        : null;

      return resumeTime
        ? `${baseMessage} ${BANNER_MESSAGES.SERVICE_RESUMES} ${resumeTime}`
        : baseMessage;
    }

    if (!menuOpen) {
      return `${BANNER_MESSAGES.MENU_UNAVAILABLE} ${
        BANNER_MESSAGES.AVAILABLE
      } ${menu ? getNextAvailableTime(menu, site.timeZone) : ""}`;
    }

    return "";
  };

  const message = getMessage();

  // Split the message into parts before and after "Opens" or "Available" or "Service will resume"
  // todo refactor: just return timeText and statusText in above function and it will be way easier to read and no regex required
  const parts = message.split(
    new RegExp(
      `(?:${BANNER_MESSAGES.OPENS}|${BANNER_MESSAGES.AVAILABLE}|${BANNER_MESSAGES.SERVICE_RESUMES})\\s`
    )
  );
  const timeText = parts[1]
    ? `${
        parts[0].includes(BANNER_MESSAGES.SERVICE_RESUMES)
          ? BANNER_MESSAGES.SERVICE_RESUMES
          : BANNER_MESSAGES.OPENS
      } ${parts[1]}`
    : "";
  const statusText = parts[0];

  const messageContent = (
    <>
      <span className={styles.status}>{statusText}</span>
      {timeText && <span className={styles.time}>{timeText}</span>}
    </>
  );

  return (
    <div data-sticky="offline-banner" className={styles.banner}>
      <div className={styles.content}>
        {messageContent}
        <span className={styles.mobileOnly}>
          <span className={styles.spacer}>•</span>
          {messageContent}
        </span>
      </div>
    </div>
  );
};

export default OfflineBanner;
